import React, { useState, useEffect } from 'react';
import type { Category, SidebarArticle } from '@jwp/ott-common/types/insights';

import { SidebarSection } from '../SidebarSection/SidebarSection';

import styles from './Sidebar.module.scss';

interface SidebarProps {
  categories: Category[];
  sidebarArticles: SidebarArticle[];
  expandedCategories: Set<Category>;
  onToggleCategory: (category: Category) => void;
  onArticleClick: (article: SidebarArticle) => void;
  isCollapsed?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ categories, sidebarArticles, expandedCategories, onToggleCategory, onArticleClick, isCollapsed = false }) => {
  const [isMobile, setIsMobile] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth <= 768;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const shouldShowCollapsed = isCollapsed && !isMobile;

  if (shouldShowCollapsed) {
    return (
      <aside className={`${styles.sidebar} ${styles.collapsed}`}>
        <div className={styles.collapsedContent}>
          {categories.map((category) => {
            const categoryArticles = sidebarArticles.filter((article) => article.category === category);
            const articleCount = categoryArticles.length;

            if (articleCount === 0) return null;

            return (
              <div
                key={category}
                className={styles.collapsedCategory}
                onClick={() => onToggleCategory(category)}
                role="button"
                tabIndex={0}
                title={`${category} (${articleCount} articles)`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onToggleCategory(category);
                  }
                }}
              >
                <div className={styles.collapsedIcon}>
                  <img width={33} height={16} src="/images/insights-list-logo.png" alt="Corrigan Sports Network" />
                </div>
                <div className={styles.collapsedLabel}>{category}</div>
                <div className={styles.articleCount}>{articleCount}</div>
              </div>
            );
          })}
        </div>
      </aside>
    );
  }

  return (
    <aside className={styles.sidebar}>
      {categories.map((category) => (
        <SidebarSection
          key={category}
          category={category}
          title={category}
          articles={sidebarArticles.filter((article) => article.category === category)}
          isExpanded={expandedCategories.has(category)}
          onToggleExpanded={onToggleCategory}
          onArticleClick={onArticleClick}
        />
      ))}
    </aside>
  );
};
